import type { ConversationStatus } from "@/api/agent-conversation";
import { useMemo } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type {
  ActivityNode,
  ConfigActivityRow,
} from "@/api/config-server-stages";
import { buildNodeAdapter } from "@/api/stage-conversations";
import { AgentConversation } from "@/components/agents/conversation/AgentConversation";
import { ExecutionTerminal } from "@/components/agents/conversation/ExecutionTerminal";
import { effectiveOrgId, type OrgTreeNode } from "@/lib/org-structure-tree";

export type ExecutionPanelProps =
  | {
      kind: "org";
      onStatusChange?: (status: ConversationStatus | null) => void;
      projectId: string;
      node: OrgTreeNode | null;
      /** A conversa aberta pelo ▶ da unidade, se houver: vale antes do último run dela. */
      liveRunId: string | null;
      /** O pai da unidade ainda não está cadastrado: sem run, o terminal explica por que o ▶ está desabilitado. */
      parentNotRegistered?: boolean;
    }
  | {
      kind: "activity";
      projectId: string;
      node: ActivityNode;
      row: ConfigActivityRow | null;
      activityCode?: string | null;
      statusRunId?: string | null;
      /** O run em andamento desta configuração, se houver. */
      liveRunId: string | null;
      /** Aba ainda não liberada: a linha sem run mostra o alerta no lugar de "Not executed yet.". */
      lockedReason?: string | null;
    };

/**
 * O console preenche o pai, igual para as duas variantes. Quem dá a altura é a tela
 * (`useFitAboveFooter`): o painel termina acima do rodapé fixo em qualquer janela.
 */
function TerminalFrame({ children }: { children: ReactNode }) {
  return <div className="flex h-full min-h-0 flex-col">{children}</div>;
}

/** Sem execução para mostrar o console continua de pé, com a linha "> …" no lugar do log. */
function EmptyTerminal({
  line,
  hint,
  title = null,
}: {
  line: string;
  hint?: string;
  title?: string | null;
}) {
  return (
    <TerminalFrame>
      <ExecutionTerminal
        source={null}
        placeholder={line}
        hint={hint}
        title={title}
      />
    </TerminalFrame>
  );
}

/** "código · nome" no cabeçalho do console. */
function consoleTitle(code: string, name: string | null | undefined): string {
  return name ? `${code} · ${name}` : code;
}

export function ExecutionPanel(props: ExecutionPanelProps) {
  return props.kind === "org" ? (
    <OrgExecution {...props} />
  ) : (
    <ActivityExecution {...props} />
  );
}

function OrgExecution({
  projectId,
  node,
  liveRunId,
  parentNotRegistered = false,
  onStatusChange,
}: Extract<ExecutionPanelProps, { kind: "org" }>) {
  const { t } = useTranslation();
  if (!node)
    return <EmptyTerminal line={t("Select a unit to see its execution.")} />;
  const title = consoleTitle(effectiveOrgId(node), node.name);
  const runId = liveRunId ?? node.lastRunId ?? null;
  if (!runId) {
    return (
      <EmptyTerminal
        title={title}
        line={t("Not executed yet.")}
        hint={
          parentNotRegistered
            ? t("Register the parent first, or use Run all.")
            : t(
                "This unit has not run yet. Only one execution runs at a time. Order: organizational structure, then SSCUI, then App Fiori.",
              )
        }
      />
    );
  }
  return (
    <ConversationTerminal
      projectId={projectId}
      node="org_structure"
      runId={runId}
      title={title}
      onStatusChange={onStatusChange}
    />
  );
}

function ActivityExecution({
  projectId,
  node,
  row,
  activityCode,
  statusRunId,
  liveRunId,
  lockedReason = null,
}: Extract<ExecutionPanelProps, { kind: "activity" }>) {
  const { t } = useTranslation();
  const code = row?.activity_code ?? activityCode;
  if (!code)
    return (
      <EmptyTerminal line={t("Select a configuration to see its execution.")} />
    );
  const title = consoleTitle(code, row?.name);
  // O run aberto pelo ▶ vale primeiro; sem ele, a última conversa da linha (o
  // histórico, ou a conversa ainda aberta). A linha já traz o run: nada de consulta
  // à parte, e uma falha de leitura nunca vira "ainda não rodou".
  const runId = liveRunId ?? statusRunId ?? row?.last_run_id ?? null;
  if (!runId)
    return <ActivityNotRun title={title} lockedReason={lockedReason} />;
  return (
    <ConversationTerminal
      projectId={projectId}
      node={node}
      runId={runId}
      title={title}
    />
  );
}

/** Sem run: "Not executed yet." (ou o alerta da aba bloqueada) e, no campo, por que espera. */
function ActivityNotRun({
  title,
  lockedReason,
}: {
  title: string;
  lockedReason: string | null;
}) {
  const { t } = useTranslation();
  return (
    <EmptyTerminal
      title={title}
      line={lockedReason ?? t("Not executed yet.")}
      hint={
        lockedReason ??
        t(
          "This configuration has not run yet. Only one configuration runs at a time. Order: organizational structure, then SSCUI, then App Fiori.",
        )
      }
    />
  );
}

function ConversationTerminal({
  onStatusChange,
  projectId,
  node,
  runId,
  title,
}: {
  projectId: string;
  onStatusChange?: (status: ConversationStatus | null) => void;
  node: ActivityNode | "org_structure";
  runId: string;
  title: string;
}) {
  const adapter = useMemo(
    () => buildNodeAdapter(projectId, node, runId),
    [projectId, node, runId],
  );
  return (
    <TerminalFrame>
      <AgentConversation
        adapter={adapter}
        title={title}
        onStatusChange={onStatusChange}
      />
    </TerminalFrame>
  );
}
